import { posthogEvents } from '@/shared/constants/constants';
import { posthogLog } from '@/shared/pinia/modules/posthog/logger';

type FilterMap = Record<string, string[]>;

const getCollectionFiltersFromUrl = (url: URL): FilterMap => {
  return Array.from(url.searchParams.entries()).reduce(
    (acc: FilterMap, [key, value]) => {
      // filter to search params that are hobbii filters
      if (!key.startsWith('filter.') || !key.includes('hobbii')) {
        return acc;
      }

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(value);
      return acc;
    },
    {}
  );
};

const getChangedFilters = (
  previousFilters: FilterMap,
  currentFilters: FilterMap
): FilterMap => {
  const changedFilters: FilterMap = {};

  const arraysAreEqual = (a: string[], b: string[]): boolean => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };

  // Check for added or changed filters
  for (const filterKey of Object.keys(currentFilters)) {
    const previousValues = previousFilters[filterKey] || [];
    const currentValues = currentFilters[filterKey] || [];
    if (!arraysAreEqual(previousValues, currentValues)) {
      changedFilters[filterKey] = [...currentValues];
    }
  }

  // Check for removed filters
  for (const filterKey of Object.keys(previousFilters)) {
    if (!(filterKey in currentFilters)) {
      changedFilters[filterKey] = [];
    }
  }

  return changedFilters;
};

const logFilterChanges = (changedFilters: FilterMap, url: URL) => {
  Object.entries(changedFilters).forEach(([key, values]) => {
    const data = {
      filter_type: key.split('.').pop(),
      filter_value: values.join(','),
      url: url.toString()
    };

    posthogLog('filter_applied', data);
    window?.posthog?.capture(posthogEvents.FILTER_APPLIED, data);
  });
};

export const trackFilterApplied = () => {
  let collectionFilters: FilterMap = {};

  const handleFilterTracking = () => {
    const url = new URL(window.location.href);
    const newCollectionFilters = getCollectionFiltersFromUrl(url);

    const changedFilters = getChangedFilters(
      collectionFilters,
      newCollectionFilters
    );

    const filtersChanged = Object.keys(changedFilters).length > 0;

    if (!filtersChanged) {
      return;
    }

    logFilterChanges(changedFilters, url);

    collectionFilters = { ...newCollectionFilters };
  };

  const filterPanel = document.querySelector(
    '#custom-filter-panel-scrollable-content'
  );

  filterPanel?.addEventListener('change', handleFilterTracking);
  filterPanel?.addEventListener('click', handleFilterTracking);
};
